// src/components/Person/WizardAddPerson.js
// Multi-step wizard for creating a person template with incomes
// V2: Full-page design with income sources + year tabs
import React, { useState, useMemo } from 'react';
import { calculateFiscalParts, MARITAL_STATUS_OPTIONS } from '../../utils/fiscalCalculations';
import { getFiscalConfig } from '../../utils/fiscalConfig';
import IncomeDetailModal from '../Income/IncomeDetailModal';
import './WizardAddPerson.css';

// Income type configuration with coherent icons
const INCOME_TYPE_CONFIG = {
  SALARIE: {
    label: 'Salaire',
    icon: 'S',
    color: '#3498DB',
    brutLabel: 'Salaire brut annuel',
    chargesRate: 0.23,
    abatementRate: 0.10,
    abatementMax: 14171,
    hasDetailFields: true,
    detailFields: ['employerName']
  },
  CHOMAGE: {
    label: 'Chômage',
    icon: 'C',
    color: '#9B59B6',
    brutLabel: 'Allocation brute annuelle',
    chargesRate: 0,
    abatementRate: 0.10,
    abatementMax: 14171,
    hasDetailFields: false
  },
  RETRAITE: {
    label: 'Retraite',
    icon: 'R',
    color: '#16A085',
    brutLabel: 'Pension brute annuelle',
    chargesRate: 0,
    abatementRate: 0.10,
    abatementMax: 4321,
    hasDetailFields: false
  },
  EI_MICRO_BNC: {
    label: 'EI Micro-BNC',
    icon: 'EI',
    color: '#27AE60',
    brutLabel: 'Chiffre d\'affaires',
    chargesRate: 0.211,
    abatementRate: 0.34,
    hasDetailFields: true,
    detailFields: ['activityDescription', 'cfeExempt']
  },
  EI_MICRO_BIC_SERVICE: {
    label: 'EI Micro-BIC Services',
    icon: 'EI',
    color: '#2ECC71',
    brutLabel: 'Chiffre d\'affaires',
    chargesRate: 0.212,
    abatementRate: 0.50,
    hasDetailFields: true,
    detailFields: ['activityDescription', 'cfeExempt']
  },
  EI_MICRO_BIC_VENTE: {
    label: 'EI Micro-BIC Vente',
    icon: 'EI',
    color: '#58D68D',
    brutLabel: 'Chiffre d\'affaires',
    chargesRate: 0.123,
    abatementRate: 0.71,
    hasDetailFields: true,
    detailFields: ['activityDescription', 'cfeExempt']
  },
  EI_REEL_BNC: {
    label: 'EI Réel BNC',
    icon: 'EI',
    color: '#1E8449',
    brutLabel: 'Chiffre d\'affaires',
    chargesRate: null,
    abatementRate: null,
    hasDetailFields: true,
    detailFields: ['charges', 'activityDescription']
  },
  EI_REEL_BIC: {
    label: 'EI Réel BIC',
    icon: 'EI',
    color: '#196F3D',
    brutLabel: 'Chiffre d\'affaires',
    chargesRate: null,
    abatementRate: null,
    hasDetailFields: true,
    detailFields: ['charges', 'activityDescription']
  },
  LMNP_MICRO: {
    label: 'LMNP Micro',
    icon: 'LM',
    color: '#E67E22',
    brutLabel: 'Loyers bruts annuels',
    chargesRate: 0,
    abatementRate: 0.50,
    hasDetailFields: false
  },
  LMNP_REEL: {
    label: 'LMNP Réel',
    icon: 'LM',
    color: '#D35400',
    brutLabel: 'Loyers bruts annuels',
    chargesRate: null,
    abatementRate: null,
    hasDetailFields: true,
    detailFields: ['charges', 'propertyAddress']
  },
  FONCIER_MICRO: {
    label: 'Foncier Micro',
    icon: 'F',
    color: '#F39C12',
    brutLabel: 'Loyers bruts annuels',
    chargesRate: 0,
    abatementRate: 0.30,
    hasDetailFields: false
  },
  FONCIER_REEL: {
    label: 'Foncier Réel',
    icon: 'F',
    color: '#D68910',
    brutLabel: 'Loyers bruts annuels',
    chargesRate: null,
    abatementRate: null,
    hasDetailFields: true,
    detailFields: ['charges', 'propertyAddress']
  },
  REMUNERATION_DIRIGEANT: {
    label: 'Dirigeant de société',
    icon: 'D',
    color: '#FFD700',
    brutLabel: 'Rémunération brute annuelle',
    chargesRate: 0.54,
    abatementRate: 0.10,
    abatementMax: 14171,
    hasDetailFields: true,
    detailFields: ['entityName', 'status'],
    isFromEntity: true
  }
};

const INCOME_CATEGORIES = [
  { id: 'emploi', label: 'Emploi', types: ['SALARIE', 'CHOMAGE', 'RETRAITE'] },
  { id: 'societe', label: 'Société', types: ['REMUNERATION_DIRIGEANT'] },
  { id: 'independant', label: 'EI (Indépendant)', types: ['EI_MICRO_BNC', 'EI_MICRO_BIC_SERVICE', 'EI_MICRO_BIC_VENTE', 'EI_REEL_BNC', 'EI_REEL_BIC'] },
  { id: 'immobilier', label: 'Immobilier', types: ['LMNP_MICRO', 'LMNP_REEL', 'FONCIER_MICRO', 'FONCIER_REEL'] }
];

const STEPS = [
  { id: 'identity', label: 'Identité', icon: '1' },
  { id: 'incomes', label: 'Revenus', icon: '2' },
  { id: 'summary', label: 'Résumé', icon: '3' }
];

const WizardAddPerson = ({ onSave, onSaveDraft, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const currentYear = new Date().getFullYear();

  // Step 1: Person data
  const [personData, setPersonData] = useState({
    first_name: '',
    last_name: '',
    fiscal_residence: 'France',
    marital_status: 'Célibataire',
    children_count: 0,
    single_parent: false,
    employment_types: []
  });

  // Step 2: Income sources
  const [incomeSources, setIncomeSources] = useState([]);
  const [expandedSourceId, setExpandedSourceId] = useState(null);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState({
    sourceIndex: null,
    year: null,
    incomeType: null,
    initialData: {}
  });

  // Helpers
  const handlePersonChange = (field, value) => {
    setPersonData(prev => ({ ...prev, [field]: value }));
  };

  const handleChildrenChange = (delta) => {
    setPersonData(prev => ({
      ...prev,
      children_count: Math.max(0, prev.children_count + delta)
    }));
  };

  const fiscalParts = useMemo(() => calculateFiscalParts(personData), [personData]);
  const showSingleParent = ['Célibataire', 'Divorcé', 'Veuf'].includes(personData.marital_status) && personData.children_count > 0;

  // Validation
  const isStep1Valid = personData.first_name.trim() && personData.last_name.trim();

  // Income source management
  const addIncomeSource = (incomeType) => {
    const newId = Date.now();
    const newSource = {
      id: newId,
      type: incomeType,
      label: '',
      startDate: '',
      endDate: '',
      yearlyData: {},
      specificFields: {}
    };
    setIncomeSources(prev => [...prev, newSource]);
    setExpandedSourceId(newId); // Auto-expand new source
  };

  // Get years covered by a specific source
  const getYearsForSource = (source) => {
    if (!source.startDate || !source.endDate) return [];
    const startYear = parseInt(source.startDate.split('-')[0]);
    const endYear = parseInt(source.endDate.split('-')[0]);
    if (isNaN(startYear) || isNaN(endYear)) return [];
    const years = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    return years;
  };

  // Toggle source expansion
  const toggleSourceExpanded = (sourceId) => {
    setExpandedSourceId(prev => prev === sourceId ? null : sourceId);
  };

  // Check if source has valid dates
  const sourceHasValidDates = (source) => {
    return source.startDate && source.endDate &&
           source.startDate.length >= 7 && source.endDate.length >= 7;
  };

  const updateIncomeSource = (index, updates) => {
    setIncomeSources(prev => {
      const newSources = prev.map((src, i) =>
        i === index ? { ...src, ...updates } : src
      );

      // Auto-expand when dates become valid
      const updatedSource = newSources[index];
      if (updatedSource && (updates.startDate !== undefined || updates.endDate !== undefined)) {
        const hasValidDates = updatedSource.startDate && updatedSource.endDate &&
          updatedSource.startDate.length >= 7 && updatedSource.endDate.length >= 7;
        if (hasValidDates) {
          setExpandedSourceId(updatedSource.id);
        }
      }

      return newSources;
    });
  };

  const removeIncomeSource = (index) => {
    setIncomeSources(prev => prev.filter((_, i) => i !== index));
  };

  // Get all years covered by income sources
  const coveredYears = useMemo(() => {
    const years = new Set();
    incomeSources.forEach(source => {
      const startYear = parseInt(source.startDate?.split('-')[0]) || currentYear;
      const endYear = parseInt(source.endDate?.split('-')[0]) || currentYear;
      for (let y = startYear; y <= endYear; y++) {
        years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [incomeSources, currentYear]);

  // Get income sources active in a specific year
  const getSourcesForYear = (year) => {
    return incomeSources.filter(source => {
      const startYear = parseInt(source.startDate?.split('-')[0]) || currentYear;
      const endYear = parseInt(source.endDate?.split('-')[0]) || currentYear;
      return year >= startYear && year <= endYear;
    }).map((source) => ({
      ...source,
      originalIndex: incomeSources.indexOf(source)
    }));
  };

  // Calculate charges and taxable for a source/year
  const calculateSourceYear = (source, year) => {
    const config = INCOME_TYPE_CONFIG[source.type];
    const yearData = source.yearlyData?.[year] || {};
    const grossAmount = yearData.grossAmount || 0;

    let charges = 0;
    let taxable = 0;

    if (config.chargesRate !== null) {
      charges = Math.round(grossAmount * config.chargesRate);
    } else {
      charges = yearData.charges || 0;
    }

    if (config.abatementRate !== null) {
      const abatement = config.abatementMax
        ? Math.min(grossAmount * config.abatementRate, config.abatementMax)
        : grossAmount * config.abatementRate;
      taxable = Math.round(grossAmount - abatement);
    } else {
      taxable = Math.max(0, grossAmount - charges);
    }

    return { grossAmount, charges, taxable };
  };

  // Update yearly data for a source with auto-recalculation
  const updateYearlyData = (sourceIndex, year, data) => {
    setIncomeSources(prev => prev.map((src, i) => {
      if (i !== sourceIndex) return src;

      const currentYearData = src.yearlyData?.[year] || {};
      const newYearData = { ...currentYearData, ...data };

      // If brut changed, recalculate dependent fields
      if (data.grossAmount !== undefined) {
        const brut = data.grossAmount || 0;
        const detailFields = currentYearData.detailFields;

        if (detailFields) {
          // Recalculate using detail fields ratios
          const cotisRate = detailFields.employeeContributions ? (detailFields.employeeContributions / (detailFields.grossAnnual || 1)) : 0.23;
          const newCotis = Math.round(brut * cotisRate);
          const mutuelle = detailFields.mutuelle || 600;
          const tickets = detailFields.ticketsRestaurant || 880;
          const totalCharges = newCotis + mutuelle + tickets;

          newYearData.charges = totalCharges;
          newYearData.detailFields = {
            ...detailFields,
            grossAnnual: brut,
            employeeContributions: newCotis,
            totalCharges: totalCharges
          };

          // Recalculate taxable
          const net = brut - totalCharges;
          if (detailFields.deductionType === 'fraisReels') {
            newYearData.taxable = Math.round(net - (detailFields.fraisReels || 0));
          } else {
            const deduction = Math.min(Math.max(net * 0.10, 495), 14171);
            newYearData.taxable = Math.round(net - deduction);
          }
        }
        // If no detail yet, taxable will be calculated in render
      }

      return {
        ...src,
        yearlyData: {
          ...src.yearlyData,
          [year]: newYearData
        }
      };
    }));
  };

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Navigation
  const canGoNext = () => {
    if (currentStep === 0) return isStep1Valid;
    if (currentStep === 1) return true; // Can always proceed from incomes
    return false;
  };

  const goNext = () => {
    if (!canGoNext()) return;
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Calculate totals for a year
  const calculateYearTotals = (year) => {
    const sources = getSourcesForYear(year);
    let totalGross = 0, totalCharges = 0, totalTaxable = 0;

    sources.forEach(source => {
      const calc = calculateSourceYear(source, year);
      totalGross += calc.grossAmount;
      totalCharges += calc.charges;
      totalTaxable += calc.taxable;
    });

    return { totalGross, totalCharges, totalTaxable };
  };

  // State for save feedback
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Prepare data for saving
  const prepareDataToSave = () => {
    const formattedIncomes = incomeSources.map(source => ({
      income_type: source.type,
      label: source.label,
      start_date: source.startDate ? `${source.startDate}-01` : null,
      end_date: source.endDate ? `${source.endDate}-28` : null,
      specific_fields: source.specificFields || {},
      yearly_amounts: Object.entries(source.yearlyData || {})
        .filter(([_, data]) => data.grossAmount > 0)
        .map(([year, data]) => ({
          year: parseInt(year),
          amount: parseFloat(data.grossAmount || 0)
        }))
    }));

    return {
      ...personData,
      incomes: formattedIncomes
    };
  };

  // Save draft (does NOT close the wizard)
  const handleSaveDraft = async () => {
    if (isSaving) return;
    if (!onSaveDraft) return; // Need the callback

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const dataToSave = prepareDataToSave();
      await onSaveDraft(dataToSave);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Final submit (saves and closes)
  const handleSubmit = async () => {
    try {
      const dataToSave = prepareDataToSave();
      await onSave(dataToSave);
    } catch (error) {
      console.error('Error creating person:', error);
    }
  };

  // Detail modal handlers
  const openDetailModal = (sourceIndex, year, incomeType) => {
    const source = incomeSources[sourceIndex];
    const yearData = source?.yearlyData?.[year] || {};

    // Map income type to the detailed modal type
    const modalType = mapIncomeTypeToModalType(incomeType);

    setDetailModalData({
      sourceIndex,
      year,
      incomeType: modalType,
      initialData: {
        grossAnnual: yearData.grossAmount || 0,
        turnover: yearData.grossAmount || 0, // For EI types
        grossRent: yearData.grossAmount || 0, // For LMNP/FONCIER
        grossPension: yearData.grossAmount || 0, // For RETRAITE
        ...yearData.detailFields
      }
    });
    setDetailModalOpen(true);
  };

  // Map wizard income types to detail modal types
  const mapIncomeTypeToModalType = (type) => {
    const mapping = {
      'SALARIE': 'SALARIE',
      'CHOMAGE': 'CHOMAGE',
      'RETRAITE': 'RETRAITE',
      'EI_MICRO_BNC': 'EI_MICRO_BNC',
      'EI_MICRO_BIC_SERVICE': 'EI_MICRO_BIC_SERVICE',
      'EI_MICRO_BIC_VENTE': 'EI_MICRO_BIC_VENTE',
      'EI_REEL_BNC': 'EI_REEL',
      'EI_REEL_BIC': 'EI_REEL',
      'LMNP_MICRO': 'LMNP_MICRO',
      'LMNP_REEL': 'LMNP_REEL',
      'FONCIER_MICRO': 'FONCIER_MICRO',
      'FONCIER_REEL': 'FONCIER_REEL'
    };
    return mapping[type] || 'SALARIE';
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailModalData({
      sourceIndex: null,
      year: null,
      incomeType: null,
      initialData: {}
    });
  };

  const saveDetailModalData = (data) => {
    const { sourceIndex, year } = detailModalData;
    if (sourceIndex === null || year === null) return;

    const source = incomeSources[sourceIndex];
    const brut = source?.yearlyData?.[year]?.grossAmount || 0;

    // Calculate taxable based on deduction type
    let taxable = 0;
    const netBeforeTax = brut - (data.totalCharges || 0);
    if (data.deductionType === 'fraisReels') {
      taxable = Math.round(netBeforeTax - (data.fraisReels || 0));
    } else {
      // Forfait 10% with min/max
      const deduction = Math.min(Math.max(netBeforeTax * 0.10, 495), 14171);
      taxable = Math.round(netBeforeTax - deduction);
    }

    // Update the yearly data with the calculated values from the modal
    setIncomeSources(prev => prev.map((src, i) => {
      if (i !== sourceIndex) return src;
      return {
        ...src,
        yearlyData: {
          ...src.yearlyData,
          [year]: {
            ...src.yearlyData?.[year],
            charges: data.totalCharges || 0,
            taxable: taxable,
            detailFields: data
          }
        }
      };
    }));
  };

  // ============ IR CALCULATION FOR SUMMARY ============

  // Calculate IR for a given year using quotient familial
  const calculateIRForYear = (year, totalTaxable, parts) => {
    const config = getFiscalConfig(year);
    if (!config) return { ir: 0, tmi: 0 };

    // Quotient familial = revenu imposable / parts
    const quotient = totalTaxable / parts;

    // Calculate tax on quotient
    let taxOnQuotient = 0;
    let tmi = 0;

    for (const bracket of config.irBrackets) {
      if (quotient > bracket.min) {
        const taxableInBracket = Math.min(quotient, bracket.max) - bracket.min;
        taxOnQuotient += taxableInBracket * bracket.rate;
        if (bracket.rate > 0) tmi = bracket.rate;
      }
    }

    // Multiply by number of parts
    let ir = Math.round(taxOnQuotient * parts);

    // Apply decote for low income
    const isCouple = ['Marié', 'Pacsé'].includes(personData.marital_status);
    const decoteThreshold = isCouple ? config.decote.coupleThreshold : config.decote.singleThreshold;
    const decoteCoef = isCouple ? config.decote.coupleCoef : config.decote.singleCoef;

    if (ir > 0 && ir < decoteThreshold) {
      const decote = decoteCoef - (ir * 0.4525);
      ir = Math.max(0, Math.round(ir - decote));
    }

    return { ir, tmi: Math.round(tmi * 100) };
  };

  // ============ RENDER FUNCTIONS ============

  const renderIdentityStep = () => (
    <div className="step-content identity-step">
      <div className="form-section">
        <h4><span className="section-icon">1</span> Identité</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Prénom *</label>
            <input
              type="text"
              value={personData.first_name}
              onChange={e => handlePersonChange('first_name', e.target.value)}
              placeholder="Prénom"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Nom *</label>
            <input
              type="text"
              value={personData.last_name}
              onChange={e => handlePersonChange('last_name', e.target.value)}
              placeholder="Nom"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4><span className="section-icon">2</span> Situation Familiale</h4>
        <div className="form-group">
          <label>Statut marital</label>
          <div className="status-cards">
            {MARITAL_STATUS_OPTIONS.map(opt => (
              <div
                key={opt.value}
                className={`status-card ${personData.marital_status === opt.value ? 'selected' : ''}`}
                onClick={() => handlePersonChange('marital_status', opt.value)}
              >
                <span className="status-icon">{opt.icon}</span>
                <span className="status-label">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Enfants à charge</label>
          <div className="children-control">
            <button type="button" className="children-btn minus" onClick={() => handleChildrenChange(-1)} disabled={personData.children_count === 0}>−</button>
            <span className="children-count">{personData.children_count}</span>
            <button type="button" className="children-btn plus" onClick={() => handleChildrenChange(1)}>+</button>
          </div>
        </div>

        {showSingleParent && (
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={personData.single_parent}
                onChange={e => handlePersonChange('single_parent', e.target.checked)}
              />
              <span>Parent isolé (+0.5 part)</span>
            </label>
          </div>
        )}

        <div className="fiscal-parts-display">
          <span className="label">Quotient familial</span>
          <span className="parts">{fiscalParts} part{fiscalParts > 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );

  const renderIncomesStep = () => {
    return (
      <div className="step-content incomes-step">
        {/* Add income buttons - one line per category */}
        <div className="income-add-section">
          {INCOME_CATEGORIES.map(category => (
            <div key={category.id} className="income-category-row">
              <span className="category-label">{category.label}</span>
              <div className="category-types">
                {category.types.map(type => (
                  <button
                    key={type}
                    type="button"
                    className="add-type-btn"
                    onClick={() => addIncomeSource(type)}
                  >
                    <span className="btn-icon" style={{ background: INCOME_TYPE_CONFIG[type].color }}>
                      {INCOME_TYPE_CONFIG[type].icon}
                    </span>
                    <span className="btn-label">{INCOME_TYPE_CONFIG[type].label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Income sources list */}
        <div className="income-sources-list">
          {incomeSources.length === 0 ? (
            <div className="no-sources-hint">
              <p>Ajoutez des sources de revenus ci-dessus</p>
            </div>
          ) : (
            incomeSources.map((source, index) => {
              const config = INCOME_TYPE_CONFIG[source.type];
              const isExpanded = expandedSourceId === source.id;
              const years = getYearsForSource(source);
              const hasValidDates = sourceHasValidDates(source);

              return (
                <div key={source.id} className={`income-source-card ${isExpanded ? 'expanded' : ''} ${!hasValidDates ? 'needs-dates' : ''}`}>
                  {/* Source header */}
                  <div className="source-header" onClick={() => hasValidDates && toggleSourceExpanded(source.id)}>
                    <span className="source-icon" style={{ background: config.color }}>
                      {config.icon}
                    </span>
                    <span className="source-type">{config.label}</span>
                    <input
                      type="text"
                      className="source-label-input"
                      placeholder="Libellé (optionnel)"
                      value={source.label}
                      onChange={e => { e.stopPropagation(); updateIncomeSource(index, { label: e.target.value }); }}
                      onClick={e => e.stopPropagation()}
                    />
                    <span className="date-sep">Du</span>
                    <input
                      type="month"
                      className="source-date"
                      value={source.startDate}
                      onChange={e => { e.stopPropagation(); updateIncomeSource(index, { startDate: e.target.value }); }}
                      onClick={e => e.stopPropagation()}
                    />
                    <span className="date-sep">Au</span>
                    <input
                      type="month"
                      className="source-date"
                      value={source.endDate}
                      onChange={e => { e.stopPropagation(); updateIncomeSource(index, { endDate: e.target.value }); }}
                      onClick={e => e.stopPropagation()}
                    />
                    {hasValidDates && (
                      <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                    )}
                    <button
                      type="button"
                      className="remove-source-btn"
                      onClick={e => { e.stopPropagation(); removeIncomeSource(index); }}
                    >×</button>
                  </div>

                  {/* Year table with all columns */}
                  {isExpanded && hasValidDates && years.length > 0 && (
                    <div className="source-years-table">
                      <table>
                        <thead>
                          <tr>
                            <th className="th-year">Année</th>
                            <th className="th-brut">{config.brutLabel || 'Brut'}</th>
                            <th className="th-detail"></th>
                            <th className="th-charges">Charges</th>
                            <th className="th-net">Net</th>
                            <th className="th-taxable">Imposable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {years.map(year => {
                            const yearData = source.yearlyData?.[year] || {};
                            const brut = yearData.grossAmount || 0;
                            const charges = yearData.charges || 0;
                            const netAvantImpot = brut - charges;
                            // Auto-calculate taxable if not set (forfait 10%)
                            const taxable = yearData.taxable || (netAvantImpot > 0 ? Math.round(netAvantImpot - Math.min(Math.max(netAvantImpot * 0.10, 495), 14171)) : 0);
                            const hasDetail = !!yearData.detailFields;

                            return (
                              <tr key={year}>
                                <td className="td-year">{year}</td>
                                <td className="td-brut">
                                  <div className={`input-wrapper ${brut === 0 ? 'needs-input' : ''}`}>
                                    <input
                                      type="number"
                                      value={yearData.grossAmount || ''}
                                      onChange={e => updateYearlyData(index, year, { grossAmount: parseFloat(e.target.value) || 0 })}
                                      onBlur={e => {
                                        const val = parseFloat(e.target.value) || 0;
                                        if (val > 0 && !hasDetail) {
                                          openDetailModal(index, year, source.type);
                                        }
                                      }}
                                      placeholder="0"
                                    />
                                    <span className="input-suffix">€</span>
                                  </div>
                                </td>
                                <td className="td-detail">
                                  <button
                                    type="button"
                                    className={`detail-btn ${hasDetail ? 'has-detail' : ''}`}
                                    onClick={() => openDetailModal(index, year, source.type)}
                                    title={hasDetail ? 'Modifier le détail' : 'Détailler les charges'}
                                  >
                                    ⚙
                                  </button>
                                </td>
                                <td className="td-charges">
                                  <div className="value-with-formula">
                                    <span className="auto-value">
                                      {charges > 0 ? `${charges.toLocaleString('fr-FR')} €` : '—'}
                                    </span>
                                    {charges > 0 && <span className="formula-hint">via ⚙</span>}
                                  </div>
                                </td>
                                <td className="td-net">
                                  <div className="value-with-formula">
                                    <span className="auto-value">
                                      {brut > 0 ? `${netAvantImpot.toLocaleString('fr-FR')} €` : '—'}
                                    </span>
                                    {brut > 0 && <span className="formula-hint">Brut - Charges</span>}
                                  </div>
                                </td>
                                <td className="td-taxable">
                                  <div className="value-with-formula">
                                    <span className="auto-value">
                                      {taxable > 0 ? `${taxable.toLocaleString('fr-FR')} €` : '—'}
                                    </span>
                                    {taxable > 0 && <span className="formula-hint">Net - 10%</span>}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Hint */}
                  {!hasValidDates && (
                    <div className="dates-hint">
                      ← Sélectionnez les dates de début et fin
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderSummaryStep = () => {
    // Calculate totals for each year: Net, IR, DansMaPoche
    const yearData = {};
    coveredYears.forEach(year => {
      const totals = calculateYearTotals(year);
      const netTotal = totals.totalGross - totals.totalCharges;
      const irCalc = calculateIRForYear(year, totals.totalTaxable, fiscalParts);
      const dansMaPoche = netTotal - irCalc.ir;

      yearData[year] = {
        net: netTotal,
        ir: irCalc.ir,
        dansMaPoche: dansMaPoche
      };
    });

    return (
      <div className="step-content summary-step">
        {/* Person header */}
        <div className="summary-person-header">
          <span className="person-name">{personData.first_name} {personData.last_name}</span>
          <span className="person-info">
            {personData.marital_status} · {fiscalParts} part{fiscalParts > 1 ? 's' : ''}
            {personData.children_count > 0 && ` · ${personData.children_count} enfant${personData.children_count > 1 ? 's' : ''}`}
          </span>
          <span className="sources-count">{incomeSources.length} source{incomeSources.length > 1 ? 's' : ''} de revenus</span>
        </div>

        {/* DansMaPoche by year */}
        {coveredYears.length > 0 ? (
          <div className="dansmapoche-years">
            {coveredYears.map(year => {
              const data = yearData[year];
              return (
                <div key={year} className="dansmapoche-year-card">
                  <div className="year-label">{year}</div>
                  <div className="year-breakdown">
                    <div className="breakdown-row net-row">
                      <span className="row-label">Net après charges</span>
                      <span className="row-value">{formatCurrency(data.net)}</span>
                    </div>
                    <div className="breakdown-row ir-row">
                      <span className="row-label">− IR estimé</span>
                      <span className="row-value negative">{formatCurrency(data.ir)}</span>
                    </div>
                    <div className="breakdown-row dansmapoche-row">
                      <span className="row-label">DansMaPoche</span>
                      <span className="row-value highlight">{formatCurrency(data.dansMaPoche)}</span>
                    </div>
                  </div>
                  <div className="monthly-equivalent">
                    soit {formatCurrency(Math.round(data.dansMaPoche / 12))}/mois
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data-message">
            <p>Aucun revenu saisi.</p>
            <p>Retournez à l'étape précédente pour ajouter des sources de revenus.</p>
          </div>
        )}

        {/* Disclaimer */}
        {coveredYears.length > 0 && (
          <div className="summary-disclaimer">
            Estimation provisoire basée uniquement sur les revenus personnels.
            Le calcul final intégrera les revenus issus des participations dans les sociétés.
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderIdentityStep();
      case 1: return renderIncomesStep();
      case 2: return renderSummaryStep();
      default: return null;
    }
  };

  // Full-page content
  return (
    <div className="wizard-add-person-page">
      <div className="wizard-topbar">
        <div className="topbar-left">
          <h3>Ajouter une personne</h3>
        </div>
        <div className="steps-indicator">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`step-indicator ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => index <= currentStep && setCurrentStep(index)}
            >
              <span className="step-number">{index < currentStep ? '✓' : index + 1}</span>
              <span className="step-label">{step.label}</span>
            </div>
          ))}
        </div>
        <div className="topbar-right">
          <button
            type="button"
            className={`save-draft-btn ${isSaving ? 'saving' : ''} ${saveSuccess ? 'success' : ''}`}
            onClick={handleSaveDraft}
            disabled={isSaving}
            title="Enregistrer"
          >
            Enregistrer
          </button>
          <button className="close-btn" onClick={onClose} title="Fermer">✕</button>
        </div>
      </div>

      <div className="wizard-body">
        {renderStepContent()}
      </div>

      <div className="wizard-footer">
        {currentStep > 0 && (
          <button type="button" className="back-btn" onClick={goBack}>
            ← Retour
          </button>
        )}
        <div className="footer-spacer"></div>
        {currentStep < STEPS.length - 1 && (
          <button
            type="button"
            className="next-btn"
            onClick={goNext}
            disabled={!canGoNext()}
          >
            Suivant →
          </button>
        )}
        {currentStep === STEPS.length - 1 && (
          <button type="button" className="submit-btn" onClick={handleSubmit}>
            Créer la personne
          </button>
        )}
      </div>

      {/* Income Detail Modal */}
      <IncomeDetailModal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        incomeType={detailModalData.incomeType}
        year={detailModalData.year}
        initialData={detailModalData.initialData}
        onSave={saveDetailModalData}
      />
    </div>
  );
};

export default WizardAddPerson;
